import { ChangeEvent, FC, useEffect, useState } from 'react';
import DeployRunModal, { DeployRunModalProps } from './DeployRunModal';
import { useDispatch, useSelector } from 'react-redux';
import { FormControlLabel, Switch } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { useTestplansChunk } from '@pages/testplans/hooks/useTestplansChunk';
import { selectTestplanType } from '@pages/testplans/store/selectors';
import { getTestplanTypeRequest } from '@pages/testplans/store';

enum TestType {
    HAND = 'hand',
    AUTO = 'auto',
    MIXED = 'mixed',
}

interface DeployRunModalForTestsProps
    extends Omit<DeployRunModalProps, 'showStepper' | 'showFirstStep' | 'additionalComponents' | 'testType'> {}

const DeployRunModalForTests = (props: DeployRunModalForTestsProps) => (
    <ComponentWithChunks
        Component={DeployRunModalForTestsComponent}
        featuresArray={['testPlans']}
        hooksArray={[useTestplansChunk]}
        {...props}
    />
);

const DeployRunModalForTestsComponent: FC<DeployRunModalForTestsProps> = (props) => {
    const { entity, entityType, open } = props;
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [skipDeployForHandTests, setSkipDeployForHandTests] = useState(false);
    const testplanType = useSelector(selectTestplanType);

    const checkTestsType = (): TestType | undefined => {
        if (entityType === 'testcase' && entity?.is_handtest !== undefined) {
            return entity.is_handtest ? TestType.HAND : TestType.AUTO;
        }
        if (entityType === 'testplan') {
            return testplanType as TestType;
        }
    };

    const testsType = checkTestsType();
    const isOnlyHandTests = testsType === TestType.HAND;
    const isOnlyAutoTests = testsType === TestType.AUTO;
    const shouldSkipDeployForHandTests = isOnlyHandTests && skipDeployForHandTests;
    const shouldShowCheckbox = !isOnlyAutoTests;
    const handleChangeSkipBuildSchemesForHandTests = (_e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
        setSkipDeployForHandTests(checked);
    };

    useEffect(() => {
        if (!open) {
            setSkipDeployForHandTests(false);
        }
    }, [open]);

    useEffect(() => {
        setSkipDeployForHandTests(false);
    }, [entity?.pk]);

    useEffect(() => {
        if (entityType === 'testplan' && entity?.pk) {
            dispatch(getTestplanTypeRequest({ testplanId: entity.pk } as any));
        }
    }, [dispatch, entityType, entity?.pk]);

    return (
        <DeployRunModal
            showStepper={!shouldSkipDeployForHandTests}
            additionalComponents={{
                checkbox: shouldShowCheckbox && (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={skipDeployForHandTests}
                                onChange={handleChangeSkipBuildSchemesForHandTests}
                            />
                        }
                        label={t('dont_build_schemes_for_hand-tests')}
                    />
                ),
            }}
            showFirstStep={!shouldSkipDeployForHandTests}
            showSecondStep={entityType !== 'testplan' && !skipDeployForHandTests}
            testType={testsType}
            {...props}
            params={{ ...props.params, skip_deploy_for_hand_tests: skipDeployForHandTests }}
        />
    );
};

export default DeployRunModalForTests;
